import {Component, ElementRef, inject, OnInit, ViewChild} from '@angular/core';
import {HttpParams} from "@angular/common/http";
import {switchMap} from "rxjs";
import {CompanyTimeZoneService} from "../../../_shared/time/company-time-zone.service";
import {companyToday, DayKey} from "../../../_shared/time/company-day";
import {EmployeeHttpService} from "../x-http-requests/employee-http.service";
import {GlobalAlertService} from "../../../_services/global-alert.service";
import {AuthenticationService} from "../../../_auth/authentication.service";
import {InlineNoticeComponent} from "../../../_ui/components/feedback/inline-notice/inline-notice.component";
import {DEMO_COMPANY_INVITATION_HINT, DEMO_COMPANY_LINK_HINT} from "../../../_shared/errors/demo-company-errors";
import {ExternalIdentityInfo, UserAuthenticators} from "../../../models/admin_owner/user/external-identity-info";
import {EmployeeDto} from "../x-models/employee-dto";
import {EmploymentStatus, employmentStartOf, employmentStatusOf} from "../x-models/employment-status";
import {ActivatedRoute, Router, RouterLink} from "@angular/router";
import {AlertLevel} from "../../../enums/alert-level";
import {AppRoutes} from "../../../enums/routes";
import {CompositionMinimal} from "../../../models/composition/composition-minimal";
import {BookingType} from "../../../models/payment/booking-type";
import {EmployeeDetailsView} from "../x-models/x-enums/employee-details-view";
import {PermissionService} from "../../../_auth/permission.service";
import {EmploymentRelationshipLinkerDto} from "../x-models/employment-relationship-linker-dto";
import {CompanyRoutes} from "../x-models/x-enums/company-routes";
import {EmployeeDetailsSalesView} from "../x-models/x-enums/employee-details-sales-view";
import {
  EmployeeSalesEventDto,
  EmployeeSalesLine,
  EmployeeSalesOrder
} from "../../company/x-models/employee-sales-event";
import {OrderEdto} from "../../../models/order/order-edto";
import {CompositionOverrideLinkerEdto} from "../../../management/composition/composition-override-linker-edto";
import {GlobalDateService} from "../../../_services/global-date.service";
import {GlobalDateFormat} from "../../../enums/global-date-format";
import {EmployeeService} from "../_services/_dto-services/employee.service";
import {PlatformScannerService} from "../../../_services/platform-scanner.service";
import {DownloadInformation} from "../../x-components/x-models/download-information";
import {ServerEndpoints} from "../../../server.endpoints";
import {AlignmentService} from "../../../_services/alignment.service";
import {EmployeeStatsExtended} from "../../../models/company/employee-stats-extended";
import {EmployeeEventResponseSummary} from "../_models/employee/employee-event-response-summary";
import {EmploymentRelationshipNl} from "../x-models/employment-relationship-nl";
import {EmployeeStatsHelper} from "../x-models/employee-stats-dto";
import {OrderHelper} from "../../location/_services/order-helper";
import {
  EmploymentRelationshipPopupComponent
} from "../x-popups/employment-relationship-popup/employment-relationship-popup.component";
import {
  EmployeeAttributePopupComponent
} from "../../x-components/employee-attribute-popup/employee-attribute-popup.component";
import {CommonModule} from "@angular/common";
import {FormsModule} from "@angular/forms";
import {DownloadComponent} from "../../x-components/download/download.component";
import {
  EmployeeSubmissionsPanelComponent
} from "../../forms/submissions/employee-submissions-panel.component";
import {EmployeeIdentificationComponent} from "../employee/employee-identification/employee-identification.component";
import {GrantHttpService} from "../access/grant-http.service";
import {
  InviteEmailDelivery, RoleGrantInvitation, grantErrorMessage, invitationCanBeResent, looksLikeEmail,
  resendErrorMessage
} from "../access/grant.models";
import {
  EmployeeInviteDialogComponent, EmployeeInviteRequest
} from "./employee-invite-dialog.component";
import {ResendConfirmDialogComponent} from "../access/resend-confirm-dialog.component";
import {describeAccountActionError} from "../../../_shared/errors/account-action-errors";
import {describeEmploymentPeriodError} from "./employment-period-errors";
import {InviteCodeDialogComponent} from "../access/invite-code-dialog.component";
import {ContactRequirementHttpService} from "../employees/contact-requirements/contact-requirement-http.service";
import {
  ContactRequirementChannelStatus,
  ContactRequirementOverviewEntry
} from "../employees/contact-requirements/contact-requirement.models";
import {contactChannelLabel} from "../employees/contact-requirements/contact-channels";
import {ContactQuickLinkHttpService} from "../contact-quick-links/contact-quick-link-http.service";
import {ContactChannelQuickLink} from "../contact-quick-links/contact-quick-link.models";
import {ContactChannelChipComponent} from "../../../_ui/components/contact-chip/contact-chip.component";

/**
 * One line of the Pflicht-Kontakte card (#558): a channel, and whichever of the two things we know about it.
 *
 * `quick` present ⇒ the card renders a {@link ContactChannelChipComponent} — the manager can press it. `quick`
 * absent ⇒ the state badge („Verknüpft" / „Nicht verknüpft"), which is all there is to say. Both may be
 * present for the same channel, and the chip wins: it carries "this is linked" as a consequence of being
 * pressable, so showing the badge beside it would restate the fact in the quieter of the two forms.
 */
interface ContactCardRow {
  readonly channel: string;
  readonly quick: ContactChannelQuickLink | null;
  readonly status: ContactRequirementChannelStatus | null;
}

@Component({
  selector: 'app-employee-details',
  templateUrl: './employee-details.component.html',
  imports: [
    CommonModule,
    FormsModule,
    EmploymentRelationshipPopupComponent,
    EmployeeAttributePopupComponent,
    RouterLink,
    DownloadComponent,
    EmployeeSubmissionsPanelComponent,
    EmployeeIdentificationComponent,
    InviteCodeDialogComponent,
    EmployeeInviteDialogComponent,
    ResendConfirmDialogComponent,
    ContactChannelChipComponent,
    InlineNoticeComponent
  ],
  styleUrls: ['./employee-details.component.scss']
})
export class EmployeeDetailsComponent implements OnInit {

  employee?: EmployeeDto;
  start: string = "";
  end: string = "";
  now = new Date();
  employeeSales?: CompositionMinimal[]
  employeeShifts?: EmployeeEventResponseSummary[]
  employeeSalesError: boolean = false;
  view: EmployeeDetailsView = EmployeeDetailsView.Shifts;
  employeeDetailsSalesView: EmployeeDetailsSalesView = EmployeeDetailsSalesView.List;
  employeeSalesEvents: EmployeeSalesEventDto[] | undefined | null = undefined;
  employmentRelationship: EmploymentRelationshipNl | undefined;
  stats: EmployeeStatsExtended | undefined;
  // employee attribute popup
  mouseEvent?: MouseEvent;
  employeeAttributePopupVisible: boolean = false;
  employeeAttributeEmployee: EmployeeDto | undefined;
  // employment relationship
  erVisible: boolean = false;
  // download
  downloadInformation: DownloadInformation = new DownloadInformation();
  downloadParams: string = "";
  protected employmentRelationships: EmploymentRelationshipLinkerDto[] | undefined;
  /**
   * This employee's required-contact-channel link state (#558) — `undefined` while unloaded/unauthorized,
   * `null` once loaded if the roster overview named nobody with this id. Read-only here: linking/unlinking is
   * the employee's own action, not something this screen can do on their behalf.
   */
  protected contactLinkStatus: ContactRequirementOverviewEntry | null | undefined = undefined;
  /**
   * This employee's consented contact channels with an openable address (#558) — the press-able half of the
   * card above. Loaded independently of {@link contactLinkStatus}, and on purpose: the two endpoints answer to
   * different role sets (a `ShiftLead` gets quick links and no compliance overview), so a card built from
   * either one alone would be blank for one of its two audiences.
   */
  protected contactQuickLinks: ContactChannelQuickLink[] = [];
  /**
   * The merged card, rebuilt whenever either half arrives. A field rather than a getter: the template renders
   * it in an `@for`, and a getter would rebuild the array — and therefore break `track`'s identity — on every
   * change-detection pass.
   */
  protected contactCardRows: ContactCardRow[] = [];
  protected readonly contactChannelLabel = contactChannelLabel;
  protected readonly N5BookingType = BookingType;
  protected readonly Routes = AppRoutes;
  protected readonly EmployeeDetailsView = EmployeeDetailsView;
  protected readonly CompanyRoutes = CompanyRoutes;
  protected readonly EmployeeDetailsSalesView = EmployeeDetailsSalesView;
  protected readonly GlobalDateFormat = GlobalDateFormat;
  protected readonly ServerEndpoints = ServerEndpoints;
  protected readonly EmployeeStatsHelper = EmployeeStatsHelper;
  protected readonly OrderHelper = OrderHelper;

  /** Shown instead of the invitation button in a demo company (#934). */
  protected readonly DemoInvitationHint = DEMO_COMPANY_INVITATION_HINT;

  /** Shown instead of the identity picker in a demo company (#934). */
  protected readonly DemoLinkHint = DEMO_COMPANY_LINK_HINT;
  private readonly companyTimeZone = inject(CompanyTimeZoneService);

  constructor(private employeeHttpService: EmployeeHttpService,
              private employeeService: EmployeeService,
              private globalAlertService: GlobalAlertService,
              private route: ActivatedRoute, private router: Router,
              public PermissionService: PermissionService,
              public GlobalDateService: GlobalDateService,
              public PlatformScannerService: PlatformScannerService,
              public AuthenticationService: AuthenticationService,
              private grantHttpService: GrantHttpService,
              private contactRequirementHttpService: ContactRequirementHttpService,
              private contactQuickLinkHttpService: ContactQuickLinkHttpService,
              public AlignmentService: AlignmentService) {
    if (PermissionService.CheckPermission(PermissionService.CombinedPermission_EmployeeDetailsView())) {
      this.view = EmployeeDetailsView.Data;
    }
  }

  isFinished(er: EmploymentRelationshipLinkerDto): boolean {
    return er.end < new Date();
  }

  // ----- employment history (periods; #140) -----

  /** Editable copy of the employment stints (yyyy-MM-dd strings for the date inputs). */
  periodEdits: { id: string; start: string; end: string }[] = [];

  private preparePeriodEdits(): void {
    this.periodEdits = (this.employee?.employmentPeriods ?? []).map(p => ({
      id: p.id,
      start: this.toDateInput(p.start),
      end: p.end ? this.toDateInput(p.end) : ''
    }));
  }

  /** Format a stored period instant as a `yyyy-MM-dd` for the date input, in the browser's local zone (matching
   *  the app-wide `date` pipe) rather than UTC — so a period boundary shows the same day the rest of the UI does. */
  private toDateInput(value: Date | string): string {
    const d = new Date(value);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  }

  /**
   * Employed, upcoming or former (#864), by the rule the roster uses too (`employmentStatusOf`). The page used
   * to test only `isEmployed`, which is false for a new hire, and so showed them as no longer employed.
   */
  protected employmentStatus(): EmploymentStatus | null {
    return this.employee ? employmentStatusOf(this.employee) : null;
  }

  /** When an upcoming employee starts; null otherwise. */
  protected employmentStart(): Date | null {
    return this.employee ? employmentStartOf(this.employee) : null;
  }

  /** Rehire the (former) employee: opens a new employment period starting today. */
  rehire(): void {
    if (!this.employee) return;
    const name = `${this.employee.firstName} ${this.employee.lastName}`;
    this.employeeHttpService.rehire(this.employee.id).subscribe({
      next: employee => {
        this.employee = employee;
        this.employeeService.Replace(employee);
        this.preparePeriodEdits();
        this.globalAlertService.createAlertBannerModel("Erfolg", `${name} wurde wieder eingestellt.`, AlertLevel.success, 3000);
        this.globalAlertService.show();
      },
      error: err => {
        // German mapped from `ApiError.code` — never `description`, which is the server's developer-facing
        // English. The refusal an administrator actually meets here is the overlap: the employment never ended.
        const message = describeEmploymentPeriodError(err, `${name} konnte nicht wieder eingestellt werden.`);
        this.globalAlertService.createAlertBannerModel("Fehler", message, AlertLevel.error, 5000);
        this.globalAlertService.show();
      }
    });
  }

  /** Persist an edited stint; the backend guards against overlaps and an end before the start. */
  savePeriod(edit: { id: string; start: string; end: string }): void {
    if (!this.employee || !edit.start) return;
    // Send the picked calendar days verbatim (yyyy-MM-dd) — the server resolves the start-of-day / end-of-day
    // boundaries in the company time zone, so this never depends on the browser's zone.
    this.employeeHttpService.updateEmploymentPeriod(this.employee.id, {
      periodId: edit.id,
      start: edit.start,
      end: edit.end || null
    }).subscribe({
      next: employee => {
        this.employee = employee;
        this.employeeService.Replace(employee);
        this.preparePeriodEdits();
        this.globalAlertService.createAlertBannerModel("Erfolg", "Beschäftigungszeitraum gespeichert.", AlertLevel.success, 3000);
        this.globalAlertService.show();
      },
      error: err => {
        // Structured ApiError body: overlap / end-before-start / period-gone each read as their own sentence
        // instead of a generic one — in GERMAN, mapped from the code. This line used to render
        // `err.error.description`, which was the specific answer in the wrong language.
        const message =
          describeEmploymentPeriodError(err, "Beschäftigungszeitraum konnte nicht gespeichert werden.");
        this.globalAlertService.createAlertBannerModel("Fehler", message, AlertLevel.error, 5000);
        this.globalAlertService.show();
      }
    });
  }

  // ----- external authenticators (admin, company-scoped) -----
  auth?: UserAuthenticators;
  allIdentities: ExternalIdentityInfo[] = [];
  selectedIdentityId: string = "";

  /**
   * Employee-link invitations for this employee, newest first — the path every administering role but the
   * platform owner uses to get somebody signed in (#279).
   */
  employeeInvitations: RoleGrantInvitation[] = [];

  /** The invitation just issued, shown once in {@link InviteCodeDialogComponent}; null while no dialog is up. */
  issuedInvitation: RoleGrantInvitation | null = null;

  /**
   * „Einladung erstellen" on the card — the button the dialog is opened from, and the element focus returns to
   * when it closes. An `ElementRef`, because focusing is a DOM act rather than a component one.
   *
   * **The decorator, not the `viewChild()` signal**, against the usual preference for the newer API: this
   * component is constructed directly with mocked services by its own spec (see the file header there), and a
   * signal query runs at construction and throws `NG0203` outside an injection context. The decorator is
   * metadata, so it costs the spec nothing — and rewriting a deliberately constructor-based suite into TestBed
   * to buy one signal would be paying in the wrong currency.
   */
  @ViewChild('inviteButton') private inviteButton?: ElementRef<HTMLButtonElement>;

  /**
   * Whether the „Einladung erstellen" dialog is open (#568).
   *
   * **The two questions an invitation asks live in that dialog, not on the card.** They used to sit inline —
   * a delivery choice and a binding switch, permanently open on a card that also carries the linked
   * authenticators and the password-login switch, competing with the list of invitations directly below them.
   * Open controls read as settings of the card; a button that opens a panel reads as one act, which is what
   * issuing an invitation is.
   *
   * The dialog owns both answers (see {@link EmployeeInviteDialogComponent}) and is mounted with `@if`, so
   * every opening is a fresh instance seeded from this employee's address. Nothing about the choices survives
   * a close, deliberately: the next invitation is a separate decision.
   */
  inviteDialogOpen: boolean = false;

  /**
   * The refusal the dialog is currently showing, or null.
   *
   * It lives here rather than in the dialog because the HTTP call does: a failed issue must keep the dialog
   * OPEN, still holding what was answered, so the remedy the sentence names can be acted on without retyping
   * anything. Cleared on every open and on every fresh attempt.
   */
  inviteDialogError: string | null = null;

  inviting: boolean = false;

  /**
   * Loads whatever the session may actually see.
   *
   * **The identity directory is fetched only for a platform owner.** It is
   * `GET /auth/account/admin/external-identities` — every account on the installation, across every tenant —
   * and it 403s for anybody else. Calling it regardless was the visible half of #418: a shard admin opening
   * this tab fired a guaranteed 403 into `console.error` and got an empty picker they could not use. The
   * picker is now absent for them and the invitation is offered instead, so there is nothing left to fail.
   */
  LoadAuthenticators(employeeId: string) {
    this.AuthenticationService.getEmployeeAuthenticatorsAdmin(employeeId).subscribe({
      next: (a) => (this.auth = a),
      error: (err) => console.error(err)
    });

    // Not for a demo company either (#934): the picker is not rendered there, so the directory would be read
    // for nothing.
    if (this.PermissionService.CanLinkEmployeeAuthenticatorById() && !this.DemoCompany) {
      this.AuthenticationService.listAllIdentities().subscribe({
        next: (ids) => (this.allIdentities = ids ?? []),
        error: (err) => console.error(err)
      });
    }

    this.LoadEmployeeInvitations(employeeId);
  }

  LoadEmployeeInvitations(employeeId: string) {
    this.grantHttpService.listEmployeeInvitations(employeeId).subscribe({
      next: (invitations) => (this.employeeInvitations = invitations ?? []),
      error: (err) => console.error(err)
    });
  }

  /**
   * Issues the one-time code, and reports what became of it.
   *
   * ### The three delivery outcomes (#568)
   *
   * The server returns the code whatever happened to the mail, so this never has to choose between telling the
   * truth and losing the invitation. The same three answers as the roster's create form, deliberately, because
   * they are the same act:
   *
   * - `Sent` — name the address it went to; the code is **not** also put on screen, because a second copy
   *   invites a second handover and the invitation can only be redeemed once.
   * - `NoAddress` / `Failed` — say that no mail went out, and open the dialog after all. Silently claiming a
   *   mail is on its way is the one outcome that leaves the employee waiting for a message that never arrives.
   *
   * With „Nur Link erzeugen" chosen, the dialog is the whole point and opens as it always did — it is the only
   * place the plaintext will ever appear, since the backend stores an unsalted hash of it.
   */
  InviteAuthenticator(answers: EmployeeInviteRequest) {
    if (!this.employee || this.inviting) return;
    this.inviting = true;
    this.inviteDialogError = null;

    // Both flags carry the ONE switch (#584), and delivery stands on the same stored address. Re-gated here
    // rather than trusted from the dialog: the dialog already refuses to emit either without an address, and
    // stating the rule again where the request is built is what makes it impossible for a future caller to
    // compose the combination the server refuses (`E_GRANT_INVITE_EMAIL_REQUIRED`).
    const usable = this.CanDeliverInviteByEmail();
    const bind = usable && answers.bindToEmail;
    const deliver = usable && answers.deliverByEmail;

    this.grantHttpService.inviteEmployeeLink(this.employee.id, {
      BindToEmployeeEmail: bind,
      DeliverByEmail: deliver,
      EnforceBoundEmail: bind
    }).subscribe({
      next: (invitation) => {
        this.inviting = false;
        // Closed FIRST: what follows is either a banner about an invitation that now exists or the code
        // dialog, and leaving the form open behind either one invites a second press of a button whose work
        // is already done.
        this.CloseInviteDialog();
        this.ReportInviteDelivery(invitation, deliver);
        this.LoadEmployeeInvitations(this.employee!.id);
      },
      error: (err) => {
        this.inviting = false;
        // The dialog stays OPEN, holding the answers, with the refusal inside it. A banner behind a closed
        // form would name a remedy („tragen Sie eine Adresse ein") for controls that are no longer on screen.
        this.inviteDialogError = grantErrorMessage(err, "Die Einladung konnte nicht erstellt werden.");
      }
    });
  }

  /** Opens the dialog. Clears the previous attempt's refusal, so a fresh panel never opens pre-failed. */
  /**
   * Whether the session's company is a demo company (#934). Its employees are fictional, so it refuses employee
   * link invitations and linked sign-in accounts; the Anmeldung tab offers neither and says why. Removing a link
   * and the password switch stay as they are.
   */
  get DemoCompany(): boolean {
    return this.AuthenticationService.isDemoCompany;
  }

  OpenInviteDialog() {
    if (this.inviting || this.DemoCompany) return;
    this.inviteDialogError = null;
    this.inviteDialogOpen = true;
  }

  /**
   * Closes it, and gives focus back to the button that opened it.
   *
   * The focus return is the half that is invisible with a mouse and unusable without one: a modal that
   * unmounts while holding focus drops the caret on `<body>`, which puts a keyboard user back at the top of
   * the document with the list they were working in somewhere below. Deferred by a frame because the button
   * is only focusable once change detection has removed the dialog.
   */
  CloseInviteDialog() {
    this.inviteDialogOpen = false;
    setTimeout(() => this.inviteButton?.nativeElement.focus());
  }

  /**
   * Says what became of the mail, and decides whether the code still has to be handed over by hand.
   *
   * Split out of {@link InviteAuthenticator} so the decision is one readable table rather than a branch inside
   * a subscription, and so it can be driven directly from a spec for each of the three outcomes.
   */
  private ReportInviteDelivery(invitation: RoleGrantInvitation, askedForMail: boolean) {
    if (askedForMail && invitation.emailDelivery === InviteEmailDelivery.Sent) {
      const address = invitation.deliveryEmail ?? invitation.boundEmail ?? "die hinterlegte Adresse";
      // The binding is stated on the success path too, not only in the dialog: on a successful send the dialog
      // never opens, so this banner is the ONLY place the issuer learns whether what they just handed out is
      // narrowed to one mailbox — and that decides whether an Apple relay recipient can accept it at all.
      const binding = invitation.enforceBoundEmail
        ? ` Nur mit ${invitation.boundEmail} einlösbar. Ein Konto mit einer anderen Adresse wird abgewiesen.`
        : "";
      this.ShowAlert("Einladung gesendet", `Die Einladung wurde an ${address} gesendet.${binding}`,
        AlertLevel.success);
      return;
    }

    if (askedForMail) {
      this.ShowAlert("Einladung nicht versendet",
        "Die Einladung konnte nicht per E-Mail versendet werden. Geben Sie den Link direkt weiter.",
        AlertLevel.warning);
    }
    this.issuedInvitation = invitation;
  }

  /**
   * Whether „Per E-Mail senden" may be offered at all (#568): there has to be an address to send to.
   *
   * `looksLikeEmail` rather than a mere presence check, and the same helper the other two screens use, so all
   * three agree on what an address is — the server decides for real and answers
   * `E_GRANT_INVITE_EMAIL_REQUIRED` if it disagrees.
   */
  CanDeliverInviteByEmail(): boolean {
    return looksLikeEmail(this.employee?.emailAddress);
  }

  /** Withdraws a pending invitation, so a code handed to the wrong person stops working now. */
  RevokeEmployeeInvitation(invitationId: string) {
    if (!this.employee) return;
    this.grantHttpService.revokeInvitation(invitationId).subscribe({
      next: () => this.LoadEmployeeInvitations(this.employee!.id),
      error: (err) => this.ShowGrantError(err, "Die Einladung konnte nicht widerrufen werden.")
    });
  }

  /** True while the invitation can still be redeemed — the only state offering a withdraw button. */
  InvitationIsPending(invitation: RoleGrantInvitation): boolean {
    return !invitation.redeemedAt && !invitation.revokedAt
      && new Date(invitation.expiration).getTime() > Date.now();
  }

  // ----- re-sending an employee-link invitation (#579) -----

  /** The invitation waiting for its re-send confirmation, or null while no prompt is up. */
  pendingResendInvitation: RoleGrantInvitation | null = null;

  /** True while a re-send is in flight — a second one would withdraw the replacement the first just made. */
  resending: boolean = false;

  /**
   * Whether this invitation may be offered „Einladung erneut senden".
   *
   * The SAME predicate the access screen asks, deliberately shared rather than re-derived from
   * {@link InvitationIsPending}: expiry is the ordinary reason to re-send and must not disqualify, while a
   * missing bound address must, because the server has nowhere to mail the replacement and refuses with
   * `E_GRANT_INVITE_EMAIL_REQUIRED`. An employee with no stored address is the common case here, so this is
   * the difference between a button that works and one that always fails.
   */
  InvitationCanBeResent(invitation: RoleGrantInvitation): boolean {
    // #934: a re-send issues a new code, which a demo company refuses like a first invitation.
    return !this.DemoCompany && invitationCanBeResent(invitation);
  }

  /** Opens the confirmation. Nothing is sent until {@link ConfirmResendInvitation} runs. */
  AskResendInvitation(invitation: RoleGrantInvitation) {
    if (this.resending) return;
    this.pendingResendInvitation = invitation;
  }

  /** Cancel, backdrop or Escape — the invitation is untouched and its code still works. */
  CancelResendInvitation() {
    this.pendingResendInvitation = null;
  }

  /**
   * Re-sends the confirmed invitation through the **employee-scoped** endpoint.
   *
   * Not the generic `/grants/invitations/{id}/resend`, even though it is the same service method behind it:
   * this screen holds an employee and a row from that employee's own list, and passing the employee id lets
   * the server check that the invitation really is theirs. A mismatch answers `E_GRANT_NOT_FOUND`, exactly as
   * an unknown id does, so nothing about it is an oracle.
   *
   * The response carries the replacement's plaintext code and the delivery outcome, so it goes into
   * {@link issuedInvitation} and the same dialog an issue uses — the server hands back the code whatever
   * became of the mail, and hiding it on a successful send would turn a filtered message into a dead end.
   */
  ConfirmResendInvitation() {
    const invitation = this.pendingResendInvitation;
    if (!this.employee || !invitation || this.resending) return;

    this.resending = true;
    this.grantHttpService.resendEmployeeInvitation(this.employee.id, invitation.id).subscribe({
      next: (fresh) => {
        this.resending = false;
        this.pendingResendInvitation = null;
        this.issuedInvitation = fresh;
        this.LoadEmployeeInvitations(this.employee!.id);
      },
      error: (err) => {
        this.resending = false;
        this.pendingResendInvitation = null;
        this.ShowResendError(err);
        // Reload anyway: the two 409s mean the row on screen is stale (somebody redeemed or withdrew it),
        // and leaving the old state under a refusal invites a second press of the same button.
        this.LoadEmployeeInvitations(this.employee!.id);
      }
    });
  }

  LinkAuthenticator() {
    if (!this.employee || !this.selectedIdentityId || this.DemoCompany) return;
    this.AuthenticationService.linkEmployeeIdentity(this.employee.id, this.selectedIdentityId).subscribe({
      next: () => {
        this.selectedIdentityId = "";
        this.LoadAuthenticators(this.employee!.id);
      },
      error: (err) => this.ShowAuthError(err)
    });
  }

  RemoveAuthenticator(identityId: string) {
    if (!this.employee) return;
    this.AuthenticationService.removeEmployeeAuthenticatorAdmin(this.employee.id, identityId).subscribe({
      next: () => this.LoadAuthenticators(this.employee!.id),
      error: (err) => this.ShowAuthError(err)
    });
  }

  ToggleAuthPassword(enabled: boolean) {
    if (!this.employee) return;
    this.AuthenticationService.setEmployeePasswordLoginAdmin(this.employee.id, enabled).subscribe({
      next: () => this.LoadAuthenticators(this.employee!.id),
      error: (err) => {
        this.ShowAuthError(err);
        this.LoadAuthenticators(this.employee!.id);
      }
    });
  }

  /**
   * Reports a failed **authenticator** action, in German mapped from the error code.
   *
   * It used to render `err.error.description` — the server's `ErrorCatalog` text, English by design and
   * written for a log rather than for a person (frontend style guide, *"Surfacing an API error"*). The
   * mapping is shared with the employee's own panel and the platform owner's user screen, which enforce the
   * identical `ExternalIdentityService` guard: an administrator and the account holder must read the same rule.
   */
  private ShowAuthError(err: any) {
    this.ShowError(describeAccountActionError(err, "Die Aktion konnte nicht ausgeführt werden."));
  }

  /**
   * Reports a failed **invitation** action.
   *
   * Split from {@link ShowAuthError} deliberately: the invitation buttons talk to a different endpoint family
   * with its own catalog block, so routing them through the authenticator vocabulary meant every grant refusal
   * — `E_EMPLOYEE_INVITE_UNKNOWN_EMPLOYEE` above all, which is checked at issue time precisely so somebody is
   * present to read it — collapsed into "Die Aktion konnte nicht ausgeführt werden.". One helper serving two
   * vocabularies answers neither of them well.
   */
  private ShowGrantError(err: unknown, fallback: string) {
    this.ShowError(grantErrorMessage(err, fallback));
  }

  /**
   * Reports a failed **re-send** (#579).
   *
   * Its own helper for the reason {@link ShowGrantError} is split from {@link ShowAuthError}: one code,
   * `E_GRANT_INVITE_EMAIL_REQUIRED`, has a sentence written for the invite FORM — it names two switches and an
   * address field, none of which exist on a re-send. {@link resendErrorMessage} overrides exactly that one and
   * delegates the rest, so the two screens still share one vocabulary.
   */
  private ShowResendError(err: unknown) {
    this.ShowError(resendErrorMessage(err, "Die Einladung konnte nicht erneut gesendet werden."));
  }

  private ShowError(message: string) {
    this.ShowAlert("Fehler", message, AlertLevel.error);
  }

  /**
   * Posts a banner and shows it.
   *
   * `createAlertBannerModel` only fills the mailbox — without the `show()` nothing renders at all (style guide,
   * "Posting is not showing"), which is why every caller goes through here rather than repeating the pair.
   */
  private ShowAlert(header: string, message: string, level: AlertLevel) {
    this.globalAlertService.createAlertBannerModel(header, message, level, 4000);
    this.globalAlertService.show();
  }

  GetEmployeeSalesSum(): number {
    if (!this.employeeSales) return 0;

    return this.employeeSales.map(x => x.quantity * x.price).reduce((sum, current) => sum + current, 0);
  }

  LoadEmployee(id: string) {
    this.employee = this.employeeService.GetById(id);
    if (!this.employee) {
      // Former employees are not in the default DTO cache (it holds the currently employed) — resolve them
      // directly, former-inclusive, so their profile with all historical data stays reachable.
      this.employeeHttpService.get(id, true).subscribe({
        next: employee => {
          this.employee = employee;
          this.preparePeriodEdits();
          this.LoadEmployeeSections(id);
        },
        error: () => {
          this.globalAlertService.createAlertBannerModel("Fehler", "Mitarbeiter konnte nicht geladen werden.", AlertLevel.error, 3000);
          this.globalAlertService.show();
          this.router.navigate([AppRoutes.CompanyEmployee]);
        }
      });
      return;
    }

    this.preparePeriodEdits();
    this.LoadEmployeeSections(id);
  }

  private LoadEmployeeSections(id: string) {
    if (this.PermissionService.CheckPermission(this.PermissionService.Company_Employee_EmployeeSalesList())) {
      this.LoadEmployeeSales(id, this.start, this.end);
    }
    // Shifts are visible to admins and to the external payroll accountant (backend grants Payroll the read).
    if (this.PermissionService.CheckPermission(this.PermissionService.Company_Employee_GetEmployeeShiftList())
        || this.PermissionService.IsPayroll()) {
      this.LoadEmployeeShifts(id, this.start, this.end)
    }

    if (this.PermissionService.CheckPermission(this.PermissionService.Company_Employee_GetEmploymentRelationships())) {
      this.loadEmploymentRelationships(id);
    }

    // Authenticators / external identities are admin-only. The gate mirrors the four `admin/employees/…`
    // actions on AccountController exactly (PlatformOwner/CompanyOwner/ShardAdmin) rather than the shared
    // employee-detail PermissionSet, which is keyed on ShardAdmin alone and therefore refused the owner of the
    // whole company — the audience the backend gate was widened for. Still guarded: without it a
    // payroll/non-admin session fires two guaranteed 403s.
    if (this.PermissionService.CanManageEmployeeAuthenticators()) {
      this.LoadAuthenticators(id);
    }

    if (this.employeeAttributeEmployee != undefined) {
      this.employeeAttributeEmployee = this.employee;
    }

    if (this.PermissionService.CheckPermission(this.PermissionService.CombinedPermission_EmployeeDetailsView())) {
      this.loadStats(id);
    }

    // Required-contact-channel link state (#558) — read-only here, so gate it the same as the manager screen
    // that configures the requirement behind it (`CanManageAccessGrants()`): a payroll session that reaches
    // this page has no business seeing, and no permission to fetch, another employee's link state.
    if (this.PermissionService.CanManageAccessGrants()) {
      this.loadContactLinkStatus(id);
    }

    // The quick links are a SEPARATE gate on a SEPARATE endpoint, and both differences matter: a `ShiftLead`
    // passes this one and not the one above, so the card has to be able to render from either half alone.
    if (this.PermissionService.CanUseContactQuickLinks()) {
      this.loadContactQuickLinks(id);
    }
  }

  /**
   * Loads this employee's consented, openable contact channels (#558) — one id, so one request; the service's
   * batching exists for the roster, and asking it for a single id is the cheap case it already handles.
   */
  private loadContactQuickLinks(id: string): void {
    this.contactQuickLinkHttpService.forEmployees([id]).subscribe({
      next: byEmployee => {
        this.contactQuickLinks = byEmployee.get(id) ?? [];
        this.rebuildContactCard();
      },
      // Silent for the same reason the link-state card's failure is: this is a convenience on a page that
      // already surfaces its own real failures, and a second banner would compete with them.
      error: () => {
        this.contactQuickLinks = [];
        this.rebuildContactCard();
      }
    });
  }

  /**
   * Merges the two halves into one row per channel.
   *
   * Channel order follows the compliance overview first (that list is the one with a server-defined order),
   * then any consented channel the overview did not mention — which is exactly what a `ShiftLead` sees, since
   * for them the overview half is always empty.
   */
  private rebuildContactCard(): void {
    const quickByChannel = new Map(this.contactQuickLinks.map(q => [q.channel, q]));
    const rows: ContactCardRow[] = [];

    for (const status of this.contactLinkStatus?.channels ?? []) {
      rows.push({channel: status.channel, status, quick: quickByChannel.get(status.channel) ?? null});
      quickByChannel.delete(status.channel);
    }
    for (const [channel, quick] of quickByChannel) {
      rows.push({channel, status: null, quick});
    }

    this.contactCardRows = rows;
  }

  /**
   * Loads this employee's entry from the manager-facing roster overview (#558). There is no per-employee
   * variant of that endpoint, so this fetches the whole roster and filters client-side — acceptable here
   * because the card is informational and the same overview is already paid for by the Pflicht-Kontakte screen.
   */
  private loadContactLinkStatus(id: string): void {
    this.contactRequirementHttpService.overview().subscribe({
      next: entries => {
        this.contactLinkStatus = entries.find(e => e.employeeId === id) ?? null;
        this.rebuildContactCard();
      },
      error: () => {
        // Silent: this card is a convenience, not the source of truth (that is `/ep/contacts` for the employee
        // themselves), and a failed fetch must not compete with the alerts the rest of this page already shows.
        this.contactLinkStatus = null;
        this.rebuildContactCard();
      }
    });
  }

  /** Tooltip for one channel badge on the read-only Pflicht-Kontakte card — same shape as the manager screen's
   * own `channelStatusHint` (`_modules/company/employees/contact-requirements/contact-requirements.component.ts`). */
  contactLinkChannelHint(status: {linked: boolean; lastVerifiedUtc?: string | null; deadline?: string | null}): string {
    if (status.linked) {
      return status.lastVerifiedUtc
        ? `Zuletzt bestätigt am ${new Date(status.lastVerifiedUtc).toLocaleDateString('de-DE')}.`
        : 'Verknüpft.';
    }
    return status.deadline
      ? `Nicht verknüpft — Frist bis ${new Date(status.deadline).toLocaleDateString('de-DE')}.`
      : 'Nicht verknüpft.';
  }

  calculateOrderTotal(order: EmployeeSalesOrder): number {
    return order.compositions.reduce((sum: number, linker: EmployeeSalesLine) => sum + linker.quantity * linker.price, 0);
  }

  calculateEventTotal(event: EmployeeSalesEventDto): number {
    return event.orders.reduce((sum: number, order: EmployeeSalesOrder) => sum += this.calculateOrderTotal(order), 0);
  }

  LoadEmployeeSales(id: string, start: string, end: string) {
    // Sales (Getränke) are admin-only. The date-picker calls this on every change, so guard here too — otherwise a
    // payroll session (which never sees the Getränke tab) would fire guaranteed 403s when changing the range.
    if (!this.PermissionService.CheckPermission(this.PermissionService.Company_Employee_EmployeeSalesList())) return;
    this.employeeHttpService.employeeSalesOrders(id, start, end).subscribe({
      next: events => {
        this.employeeSalesEvents = events;
      },
      error: error => {
        console.error(error);
        this.employeeSalesEvents = null;
        this.globalAlertService.createAlertBannerModel("Fehler", "Mitarbeiterverkäufe nach Events konnten nicht geladen werden.", AlertLevel.error, 2000);
        this.globalAlertService.show();
      }
    })

    this.employeeHttpService.employeeSalesList(id, start, end).subscribe(x => {
      this.employeeSalesError = false;
      this.employeeSales = x;
    }, error => {
      this.employeeSales = undefined;
      this.employeeSalesError = true;
      //this.globalAlertService.createAlertBannerModel("Fehler", "Mitarbeiterverkäufe konnten nicht geladen werden.", AlertLevel.error, 3000);
      //this.globalAlertService.show()
    })
  }

  /**
   * Loads the employee's shifts for the calendar days `[start, end]` (`yyyy-MM-dd`, both inclusive). The days go to
   * the server verbatim and are resolved there in the company time zone (#645) — parsing them here with
   * `new Date("yyyy-MM-dd")` read them as UTC midnight, and the old `setHours`/`+1 day` math then shifted them into
   * the browser's zone.
   */
  LoadEmployeeShifts(id: string, start: DayKey, end: DayKey) {
    this.employeeHttpService.getEmployeeShiftList(id, start, end).subscribe({
      next: summary => {
        this.employmentRelationship = summary.employmentRelationship;
        this.employeeShifts = summary.employeeEventResponses;
      },
      error: err => {
        console.error(err)
        // Clear the loading state (an empty list renders the "Keine Ergebnisse" placeholder) so the shifts
        // section never hangs on its spinner when the request fails.
        this.employeeShifts = [];
        this.globalAlertService.createAndShow("Fehler", "Arbeitszeiten konnten nicht geladen werden.", AlertLevel.error);
      }
    })
  }

  loadStats(employeeId: string) {
    this.employeeHttpService.getStats(employeeId).subscribe({
      next: x => {
        this.stats = x;
      },
      error: error => {
        console.error(error);
        this.globalAlertService.createAndShow("Fehler", "Informationen konnten nicht geladen werden.", AlertLevel.error);
      }
    })
  }

  getAverageHoursPerMonth(firstShift: Date | undefined, hoursWorked: number | undefined): number {
    if (!firstShift || !hoursWorked) return 0;
    let now: Date = new Date();

    // +1 because we want to include the current month
    let months = now.getFullYear() * 12 + now.getMonth() - firstShift.getFullYear() * 12 - firstShift.getMonth() + 1;

    return hoursWorked / months;
  }

  openPopup(e: any) {
    this.employeeAttributeEmployee = this.employee;
    this.mouseEvent = e;
    this.employeeAttributePopupVisible = true;
  }

  /**
   * Today in the COMPANY's zone, the default for both range ends. `new Date().toISOString().split("T")[0]` was the
   * UTC day, so between 00:00 and 02:00 Berlin time the page defaulted to yesterday (#645).
   */
  private companyToday(): DayKey {
    return companyToday(this.companyTimeZone.timeZone());
  }

  ngOnInit(): void {
    this.route.queryParams.subscribe(params => {
      if (params["start"]) this.start = params["start"];
      if (params["end"]) this.end = params["end"];
    })

    // Wait for the company zone before defaulting the range, so the first load already asks for the right day.
    // `load()` never errors and is cached app-wide, so this costs one request per session at most.
    this.companyTimeZone.load().pipe(switchMap(() => this.route.queryParams)).subscribe(params => {
      if (!params.id) {
        this.globalAlertService.createAlertBannerModel("Fehler", "Fehlerhafte Parameter beim Seitenaufruf, Weiterleitung zur Übersicht.", AlertLevel.error, 3000);
        this.globalAlertService.show();
        this.router.navigate([AppRoutes.CompanyEmployee])
      } else {
        this.start = params.start ?? this.companyToday();
        this.end = params.end ?? this.companyToday();

        // Deep-link straight to the Formulare tab (the payroll export's link + the submission-detail back arrow).
        // An external Payroll accountant also follows this link, so the gate is the submissions-aware check.
        if (params.view === 'submissions' && this.PermissionService.CanViewEmployeeSubmissions()) {
          this.view = EmployeeDetailsView.Submissions;
        }

        this.employeeService.EntitiesChanged.subscribe(x => {
          if (!x || x.length == 0) return;
          this.LoadEmployee(params.id)
        })
      }
    })
  }

  getDrinkCount(sales: CompositionMinimal[]): number {
    return sales.reduce((sum, current) => sum + current.quantity, 0);
  }

  getTotalSalary(): number {
    if (!this.employeeShifts) return 0;
    return this.employeeShifts
      .filter(x => x.stats)
      .reduce((sum, current) => sum + EmployeeStatsHelper.getTotal(current.stats!), 0);
  }

  getTotalHours(): number {
    if (!this.employeeShifts) return 0;
    return this.employeeShifts
      .filter(x => x.stats)
      .reduce((sum, current) => sum + (current.stats?.hoursWorked ?? 0), 0);
  }

  erReloadEmployee() {
    if (this.erVisible) return;

    this.route.queryParams.subscribe(params => {
      if (!params.id) {
        this.globalAlertService.createAlertBannerModel("Fehler", "Fehlerhafte Parameter beim Seitenaufruf, Weiterleitung zur Übersicht.", AlertLevel.error, 3000);
        this.globalAlertService.show();
        this.router.navigate([AppRoutes.CompanyEmployee])
      } else {
        this.start = params.start ?? this.companyToday();
        this.end = params.end ?? this.companyToday();

        this.LoadEmployee(params.id)
      }
    })
  }

  /**
   * Query string of the individual-details export: the same inclusive calendar days `[start, end]` the page shows,
   * sent verbatim. The server resolves them in the company zone and adds the day after `end` itself (#645), so no
   * `+1 day` and no browser-zone midnight here.
   */
  getDownloadParams(): string {
    if (!this.employee) return "";
    return new HttpParams({fromObject: {id: this.employee.id, start: this.start, end: this.end}}).toString();
  }

  redirectToSettings() {
    if (!this.employee) return;
    this.router.navigate([AppRoutes.CompanyEmployeeSettings], {queryParams: {id: this.employee.id, dv: true}}).then();
  }

  private loadEmploymentRelationships(id: string) {
    this.employeeHttpService.getEmploymentRelationships(id).subscribe({
      next: relationships => {
        this.employmentRelationships = relationships;
      },
      error: err => {
        this.employmentRelationships = undefined;
        console.error(err);
        this.globalAlertService.createAndShow("Fehler", "Arbeitsverhältnisse konnten nicht geladen werden.", AlertLevel.error);
      }
    })
  }
}
